import { useState, useEffect } from 'react';
import { useParams } from 'wouter';
import { useQuery, useMutation } from '@tanstack/react-query';
import { format, addDays, startOfWeek, isWeekend, isBefore, startOfDay } from 'date-fns';
import { Calendar, Clock, Truck, Package, CheckCircle2, Upload, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { apiRequest } from '@/lib/queryClient';

interface SchedulingLinkInfo {
  orderId: string;
  orderNumber: string;
  warehouseCode: string;
  logisticsContactEmail: string;
  logisticsContactName: string | null;
  warehouse: {
    locationName: string;
    address: string;
    city: string;
    region: string;
  } | null;
  hasAppointment: boolean;
  existingAppointment: {
    scheduledDate: string;
    scheduledTimeSlot: string;
    transportType: string;
    carrierName: string | null;
    confirmedAt: string;
  } | null;
}

interface TimeSlot {
  time: string;
  displayTime: string;
  available: boolean;
}

interface AvailableSlotsResponse {
  date: string;
  warehouseCode: string;
  slots: TimeSlot[];
}

export default function SchedulePickupPage() {
  const { token } = useParams<{ token: string }>();
  const { toast } = useToast();
  
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [transportType, setTransportType] = useState<'OWN_TRUCK' | 'CARRIER'>('OWN_TRUCK');
  const [carrierName, setCarrierName] = useState('');
  const [carrierContact, setCarrierContact] = useState('');
  const [bolUrl, setBolUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [bookedByName, setBookedByName] = useState('');
  const [bookedByEmail, setBookedByEmail] = useState('');
  const [bookedByPhone, setBookedByPhone] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    return startOfWeek(today, { weekStartsOn: 1 });
  });
  const [isSuccess, setIsSuccess] = useState(false);

  const { data: linkInfo, isLoading: isLoadingLink, error: linkError } = useQuery<SchedulingLinkInfo>({
    queryKey: ['/api/pickup-scheduling/link', token],
    queryFn: async () => {
      const response = await fetch(`/api/pickup-scheduling/link/${token}`);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch scheduling link');
      }
      return response.json();
    },
    enabled: !!token,
    retry: false,
  });

  const { data: slotsData, isLoading: isLoadingSlots } = useQuery<AvailableSlotsResponse>({
    queryKey: ['/api/pickup-scheduling/available-slots', selectedDate?.toISOString(), linkInfo?.warehouseCode],
    queryFn: async () => {
      if (!selectedDate || !linkInfo) return { date: '', warehouseCode: '', slots: [] };
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await fetch(`/api/pickup-scheduling/available-slots?date=${dateStr}&warehouseCode=${linkInfo.warehouseCode}&token=${token}`);
      if (!response.ok) {
        throw new Error('Failed to fetch available slots');
      }
      return response.json();
    },
    enabled: !!selectedDate && !!linkInfo && !linkInfo.hasAppointment,
  });

  const createAppointmentMutation = useMutation({
    mutationFn: async () => {
      if (!selectedDate || !selectedSlot) {
        throw new Error('Please select a date and time');
      }
      
      const response = await apiRequest('POST', '/api/pickup-scheduling/appointments', {
        token,
        scheduledDate: selectedDate.toISOString(),
        scheduledTimeSlot: selectedSlot,
        transportType,
        carrierName: transportType === 'CARRIER' ? carrierName : undefined,
        carrierContact: transportType === 'CARRIER' ? carrierContact : undefined,
        bolUrl: bolUrl || undefined,
        notes: notes || undefined,
        bookedByName: bookedByName || undefined,
        bookedByEmail: bookedByEmail || undefined,
        bookedByPhone: bookedByPhone || undefined,
      });
      
      return response;
    },
    onSuccess: () => {
      setIsSuccess(true);
      toast({
        title: "Appointment Scheduled",
        description: "Your pickup appointment has been confirmed.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to Schedule",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleBolUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('token', token || '');
      
      const response = await fetch('/api/pickup-scheduling/upload-bol', {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload file');
      }
      
      const { url } = await response.json();
      setBolUrl(url);
      toast({
        title: "File Uploaded",
        description: "Bill of Lading has been uploaded successfully.",
      });
    } catch (error) {
      toast({
        title: "Upload Failed",
        description: "Failed to upload Bill of Lading. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const getWeekDays = () => {
    const days = [];
    const today = startOfDay(new Date());
    
    for (let i = 0; i < 5; i++) {
      const day = addDays(currentWeekStart, i);
      const isPast = isBefore(day, today);
      days.push({
        date: day,
        dayName: format(day, 'EEE'),
        dayNumber: format(day, 'd'),
        monthName: format(day, 'MMM'),
        isDisabled: isWeekend(day) || isPast,
        isToday: format(day, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd'),
      });
    }
    return days;
  };

  const goToPreviousWeek = () => {
    setCurrentWeekStart(prev => addDays(prev, -7));
    setSelectedDate(null);
    setSelectedSlot(null);
  };

  const goToNextWeek = () => {
    setCurrentWeekStart(prev => addDays(prev, 7));
    setSelectedDate(null);
    setSelectedSlot(null);
  };

  const canGoPrevious = () => {
    const today = startOfDay(new Date());
    const prevWeekStart = addDays(currentWeekStart, -7);
    return !isBefore(addDays(prevWeekStart, 4), today);
  };

  if (isLoadingLink) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container max-w-3xl mx-auto px-4">
          <Skeleton className="h-12 w-48 mx-auto mb-8" />
          <Card>
            <CardHeader>
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-4 w-96 mt-2" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-48 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (linkError || !linkInfo) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardHeader className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <CardTitle className="text-xl text-red-600">Link Not Valid</CardTitle>
            <CardDescription className="text-base">
              {(linkError as Error)?.message || 'This scheduling link is invalid, expired, or has already been used.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-gray-500">
              If you believe this is an error, please contact OpenBox by ShopJimmy support.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (linkInfo.hasAppointment || isSuccess) {
    const appointment = linkInfo.existingAppointment || (isSuccess ? {
      scheduledDate: selectedDate?.toISOString(),
      scheduledTimeSlot: selectedSlot,
      transportType,
      carrierName: transportType === 'CARRIER' ? carrierName : null,
      confirmedAt: new Date().toISOString(),
    } : null);

    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardHeader className="text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <CardTitle className="text-xl text-green-600">Pickup Scheduled</CardTitle>
            <CardDescription className="text-base">
              Your pickup appointment has been confirmed.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <Label className="text-sm text-gray-500">Order Number</Label>
                <p className="font-semibold text-lg">{linkInfo.orderNumber}</p>
              </div>
              
              {appointment && (
                <>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <Label className="text-sm text-gray-500">Pickup Date & Time</Label>
                    <p className="font-semibold">
                      {appointment.scheduledDate && format(new Date(appointment.scheduledDate), 'EEEE, MMMM d, yyyy')}
                    </p>
                    <p className="text-gray-600">{appointment.scheduledTimeSlot}</p>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4">
                    <Label className="text-sm text-gray-500">Transport Method</Label>
                    <p className="font-semibold">
                      {appointment.transportType === 'OWN_TRUCK' ? 'Own Truck' : `Carrier: ${appointment.carrierName}`}
                    </p>
                  </div>
                </>
              )}
              
              {linkInfo.warehouse && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <Label className="text-sm text-gray-500">Pickup Location</Label>
                  <p className="font-semibold">{linkInfo.warehouse.locationName}</p>
                  <p className="text-gray-600 text-sm">{linkInfo.warehouse.address}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const weekDays = getWeekDays();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container max-w-3xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Schedule Your Pickup</h1>
          <p className="text-gray-600">
            Order <span className="font-semibold text-primary">{linkInfo.orderNumber}</span>
          </p>
        </div>

        {linkInfo.warehouse && (
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <Package className="w-6 h-6 text-primary shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold">Pickup Location</h3>
                  <p className="text-gray-600">{linkInfo.warehouse.locationName}</p>
                  <p className="text-gray-500 text-sm">{linkInfo.warehouse.address}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Select Date
            </CardTitle>
            <CardDescription>
              Pickups are available Monday through Friday
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={goToPreviousWeek}
                disabled={!canGoPrevious()}
                data-testid="button-prev-week"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm font-medium text-gray-600">
                {format(currentWeekStart, 'MMMM yyyy')}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={goToNextWeek}
                data-testid="button-next-week"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            
            <div className="grid grid-cols-5 gap-2">
              {weekDays.map((day) => (
                <button
                  key={day.date.toISOString()}
                  onClick={() => {
                    if (!day.isDisabled) {
                      setSelectedDate(day.date);
                      setSelectedSlot(null);
                    }
                  }}
                  disabled={day.isDisabled}
                  className={cn(
                    "p-3 rounded-lg border text-center transition-all",
                    day.isDisabled && "opacity-50 cursor-not-allowed bg-gray-100",
                    !day.isDisabled && "hover:border-primary cursor-pointer",
                    selectedDate && format(selectedDate, 'yyyy-MM-dd') === format(day.date, 'yyyy-MM-dd') 
                      && "border-primary bg-primary/10 ring-2 ring-primary",
                    day.isToday && !day.isDisabled && "border-blue-500"
                  )}
                  data-testid={`button-date-${format(day.date, 'yyyy-MM-dd')}`}
                >
                  <div className="text-xs text-gray-500">{day.dayName}</div>
                  <div className="text-lg font-semibold">{day.dayNumber}</div>
                  <div className="text-xs text-gray-500">{day.monthName}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {selectedDate && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Select Time Slot
              </CardTitle>
              <CardDescription>
                {format(selectedDate, 'EEEE, MMMM d, yyyy')} - Available slots (8:00 AM - 3:00 PM)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingSlots ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {Array.from({ length: 14 }).map((_, i) => (
                    <Skeleton key={i} className="h-10" />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {slotsData?.slots.map((slot) => (
                    <button
                      key={slot.time}
                      onClick={() => slot.available && setSelectedSlot(slot.time)}
                      disabled={!slot.available}
                      className={cn(
                        "p-2 rounded-lg border text-center text-sm transition-all",
                        !slot.available && "opacity-50 cursor-not-allowed bg-gray-100 line-through",
                        slot.available && "hover:border-primary cursor-pointer",
                        selectedSlot === slot.time && "border-primary bg-primary/10 ring-2 ring-primary"
                      )}
                      data-testid={`button-slot-${slot.time}`}
                    >
                      {slot.displayTime}
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {selectedSlot && (
          <>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="w-5 h-5" />
                  Transport Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup
                  value={transportType}
                  onValueChange={(value) => setTransportType(value as 'OWN_TRUCK' | 'CARRIER')}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="OWN_TRUCK" id="own-truck" data-testid="radio-own-truck" />
                    <Label htmlFor="own-truck" className="cursor-pointer">
                      I'm picking up with my own truck
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="CARRIER" id="carrier" data-testid="radio-carrier" />
                    <Label htmlFor="carrier" className="cursor-pointer">
                      A carrier will pick up on my behalf
                    </Label>
                  </div>
                </RadioGroup>

                {transportType === 'CARRIER' && (
                  <div className="space-y-4 pt-4 border-t">
                    <div>
                      <Label htmlFor="carrier-name">Carrier Name *</Label>
                      <Input
                        id="carrier-name"
                        value={carrierName}
                        onChange={(e) => setCarrierName(e.target.value)}
                        placeholder="e.g., FedEx Freight, XPO Logistics"
                        data-testid="input-carrier-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="carrier-contact">Carrier Contact (optional)</Label>
                      <Input
                        id="carrier-contact"
                        value={carrierContact}
                        onChange={(e) => setCarrierContact(e.target.value)}
                        placeholder="Phone number or email"
                        data-testid="input-carrier-contact"
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Bill of Lading (Optional)
                </CardTitle>
                <CardDescription>
                  Upload your BOL document if required by your carrier
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={handleBolUpload}
                    disabled={isUploading}
                    data-testid="input-bol-upload"
                  />
                  {isUploading && (
                    <p className="text-sm text-gray-500">Uploading...</p>
                  )}
                  {bolUrl && (
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <CheckCircle2 className="w-4 h-4" />
                      BOL uploaded successfully
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Your Contact Information</CardTitle>
                <CardDescription>
                  Please provide your contact details for the pickup
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="booked-by-name">Your Name</Label>
                  <Input
                    id="booked-by-name"
                    value={bookedByName}
                    onChange={(e) => setBookedByName(e.target.value)}
                    placeholder="Full name"
                    data-testid="input-booked-by-name"
                  />
                </div>
                <div>
                  <Label htmlFor="booked-by-email">Your Email</Label>
                  <Input
                    id="booked-by-email"
                    type="email"
                    value={bookedByEmail}
                    onChange={(e) => setBookedByEmail(e.target.value)}
                    placeholder="email@example.com"
                    data-testid="input-booked-by-email"
                  />
                </div>
                <div>
                  <Label htmlFor="booked-by-phone">Your Phone</Label>
                  <Input
                    id="booked-by-phone"
                    type="tel"
                    value={bookedByPhone}
                    onChange={(e) => setBookedByPhone(e.target.value)}
                    placeholder="(555) 555-5555"
                    data-testid="input-booked-by-phone"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Additional Notes</CardTitle>
                <CardDescription>
                  Any special instructions or requirements
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g., Need forklift assistance, specific loading dock, etc."
                  rows={3}
                  data-testid="textarea-notes"
                />
              </CardContent>
            </Card>

            <Card className="mb-8">
              <CardContent className="pt-6">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-center sm:text-left">
                    <p className="font-medium">
                      {format(selectedDate!, 'EEEE, MMMM d, yyyy')}
                    </p>
                    <p className="text-gray-600">{selectedSlot}</p>
                    <p className="text-sm text-gray-500">
                      {transportType === 'OWN_TRUCK' ? 'Own Truck' : `Carrier: ${carrierName || 'Not specified'}`}
                    </p>
                  </div>
                  <Button
                    size="lg"
                    onClick={() => createAppointmentMutation.mutate()}
                    disabled={
                      createAppointmentMutation.isPending ||
                      !selectedDate ||
                      !selectedSlot ||
                      (transportType === 'CARRIER' && !carrierName)
                    }
                    data-testid="button-confirm-appointment"
                  >
                    {createAppointmentMutation.isPending ? 'Scheduling...' : 'Confirm Pickup Appointment'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
